import React from 'react'
import {
    Box,
    Container,
    Spinner,
    VStack,
    Heading,
    Text,
    SimpleGrid,
    Button,
    useToast,
    Image,
    Input,
    FormControl,
    FormLabel,
    Icon,
    Badge,
    Divider,
    HStack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Card,
    CardBody,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
} from '@chakra-ui/react'
import { useContext, useState, useEffect, useRef } from 'react'
import { HexColorPicker } from 'react-colorful'
import { FaUpload, FaTrash, FaCheck, FaPalette, FaImage } from 'react-icons/fa'
import Navbar from '../compnents/Navbar'
import SideBar from '../compnents/SideBar'
import Footer from '../compnents/Footer'
import AuthContext from '../context/AuthContext'
import ThemeContext from '../context/ThemeContext'
import axios from 'axios'
import UploadYourFile from './UploadYourFile'
import Loading from '../compnents/Loading'
import FeatureUnlockContext from '../context/FeatureUnlockContext'

const isProduction = process.env.NODE_ENV === "production"
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV

function Personalization() {
    const { token, user, setUser, logout } = useContext(AuthContext)
    const { themeData, logo } = useContext(ThemeContext)
    const { resetUnlocks } = useContext(FeatureUnlockContext)
    const [loading, setLoading] = useState(true)
    const [themes, setThemes] = useState([])
    const [currentThemeId, setCurrentThemeId] = useState('default')
    const [customColors, setCustomColors] = useState({
        primary: '#2B6CB0',
        secondary: '#2C5282',
        accent: '#3182CE',
        background: '#F7FAFC',
        text: '#1A202C',
    })
    const [previewColors, setPreviewColors] = useState(null)
    const [logoFile, setLogoFile] = useState(null)
    const [logoPreview, setLogoPreview] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [savingTheme, setSavingTheme] = useState(false)
    const fileInputRef = useRef(null)
    const toast = useToast()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [activeColorPicker, setActiveColorPicker] = useState(null)

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0'
    const secondaryColor = themeData?.colors?.secondary || '#2C5282'
    const accentColor = themeData?.colors?.accent || '#63B3ED'
    const backgroundColor = themeData?.colors?.background || '#F7FAFC'

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight'

    // Theme-aware colors
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600'
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500'
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800'
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700'
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white'
    const inputBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'white'
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200'
    const activeBorderColor = isDarkTheme ? accentColor : primaryColor
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor
    const tabSelectedColor = isDarkTheme ? accentColor : primaryColor
    const pickerBg = isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : 'white'

    useEffect(() => {
        const fetchUser = async () => {
            if (!token || user) {
                setLoading(false)
                return
            }

            try {
                const res = await axios.get(`${API_BASE_URL}/auth/check-user`, {
                    headers: { Authorization: `Bearer ${token}` },
                })

                console.log("checking user: ", res)

                if (res.status === 420) {
                    console.error('Invalid token')
                    resetUnlocks()
                    logout()
                    return
                }

                setUser(res.data)
            } catch (error) {
                console.error('Error fetching user:', error)
                if (error.response?.status === 420) {
                    console.error('Token expired or invalid, logging out...')
                    resetUnlocks()
                    logout()
                }
            } finally {
                setLoading(false)
            }
        }

        fetchUser()
    }, [token, user, setUser, logout, resetUnlocks])

    // Fetch available themes
    useEffect(() => {
        const fetchThemes = async () => {
            if (!token) return

            try {
                const res = await axios.get(`${API_BASE_URL}/themeing/themes`, {
                    headers: { Authorization: `Bearer ${token}` },
                })

                setThemes(res.data.themes)
                setCurrentThemeId(res.data.currentTheme)

                // If current theme is custom, fetch custom colors
                if (res.data.currentTheme === 'custom') {
                    const customRes = await axios.get(`${API_BASE_URL}/themeing/custom-theme`, {
                        headers: { Authorization: `Bearer ${token}` },
                    })
                    if (customRes.data.theme?.colors) {
                        setCustomColors(customRes.data.theme.colors)
                    }
                }
            } catch (error) {
                console.error('Error fetching themes:', error)
                toast({
                    title: 'Error loading themes',
                    description: error.response?.data?.message || 'Please try again',
                    status: 'error',
                    duration: 3000,
                })
            }
        }

        if (user && user.subscription === 'business' && user.role === 'admin') {
            fetchThemes()
        }
    }, [token, user, toast])

    const handleThemeSelect = async (themeId) => {
        if (themeId === currentThemeId) return

        try {
            setSavingTheme(true)
            // eslint-disable-next-line
            const res = await axios.put(
                `${API_BASE_URL}/themeing/theme`,
                { themeId },
                { headers: { Authorization: `Bearer ${token}` } }
            )

            setCurrentThemeId(themeId)
            setPreviewColors(null)
            
            toast({
                title: 'Theme updated!',
                description: 'Your theme has been changed successfully. Refresh to see changes.',
                status: 'success',
                duration: 3000,
            })

            // Refresh page to apply new theme
            setTimeout(() => window.location.reload(), 1500)
        } catch (error) {
            console.error('Error updating theme:', error)
            toast({
                title: 'Error updating theme',
                description: error.response?.data?.message || 'Please try again',
                status: 'error',
                duration: 3000,
            })
        } finally {
            setSavingTheme(false)
        }
    }

    const handleCustomColorChange = (colorKey, value) => {
        setCustomColors(prev => ({
            ...prev,
            [colorKey]: value
        }))
        // Update preview
        setPreviewColors({
            ...customColors,
            [colorKey]: value
        })
    }

    const handleSaveCustomTheme = async () => {
        try {
            setSavingTheme(true)
            // eslint-disable-next-line
            const res = await axios.put(
                `${API_BASE_URL}/themeing/custom-theme`,
                customColors,
                { headers: { Authorization: `Bearer ${token}` } }
            )

            setCurrentThemeId('custom')
            setPreviewColors(null)
            
            toast({
                title: 'Custom theme saved!',
                description: 'Your custom colors have been applied. Refresh to see changes.',
                status: 'success',
                duration: 3000,
            })

            // Refresh page to apply new theme
            setTimeout(() => window.location.reload(), 1500)
        } catch (error) {
            console.error('Error saving custom theme:', error)
            toast({
                title: 'Error saving theme',
                description: error.response?.data?.message || 'Please try again',
                status: 'error',
                duration: 3000,
            })
        } finally {
            setSavingTheme(false)
        }
    }

    const handleLogoSelect = (e) => {
        const file = e.target.files[0]
        if (!file) return

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
        if (!allowedTypes.includes(file.type)) {
            toast({
                title: 'Invalid file type',
                description: 'Please upload a JPEG, PNG, GIF, or WebP image',
                status: 'error',
                duration: 3000,
            })
            return
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            toast({
                title: 'File too large',
                description: 'Please upload an image smaller than 5MB',
                status: 'error',
                duration: 3000,
            })
            return
        }

        setLogoFile(file)
        setLogoPreview(URL.createObjectURL(file))
    }

    const handleLogoUpload = async () => {
        if (!logoFile) return

        try {
            setUploading(true)
            const formData = new FormData()
            formData.append('logo', logoFile)

            const res = await axios.post(
                `${API_BASE_URL}/themeing/logo`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            )

            toast({
                title: 'Logo uploaded!',
                description: 'Your logo has been updated successfully',
                status: 'success',
                duration: 3000,
            })

            // Update user object
            setUser(prev => ({ ...prev, logo: res.data.logoUrl }))
            setLogoFile(null)
            setLogoPreview(null)
        } catch (error) {
            console.error('Error uploading logo:', error)
            toast({
                title: 'Error uploading logo',
                description: error.response?.data?.message || 'Please try again',
                status: 'error',
                duration: 3000,
            })
        } finally {
            setUploading(false);
            window.location.reload();
        }
    }

    const handleLogoDelete = async () => {
        try {
            setUploading(true)
            await axios.delete(`${API_BASE_URL}/themeing/logo`, {
                headers: { Authorization: `Bearer ${token}` },
            })

            toast({
                title: 'Logo deleted',
                description: 'Your logo has been removed',
                status: 'success',
                duration: 3000,
            })

            setUser(prev => ({ ...prev, logo: null }))
            setLogoFile(null)
            setLogoPreview(null)
        } catch (error) {
            console.error('Error deleting logo:', error)
            toast({
                title: 'Error deleting logo',
                description: error.response?.data?.message || 'Please try again',
                status: 'error',
                duration: 3000,
            })
        } finally {
            setUploading(false);
            window.location.reload();
        }
    }

    const handlePreviewTheme = (theme) => {
        setPreviewColors(theme.colors)
        onOpen()
    }

    if (!token || (!user && loading)) {
        return <Loading />
    }

    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center" bg={backgroundColor}>
                <Spinner size="xl" color={isDarkTheme ? accentColor : primaryColor} />
            </Box>
        )
    }

    if (user && user.noOfFileUploads === 0) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
                <Navbar />
                <UploadYourFile setUser={setUser} token={token} />
            </Box>
        )
    }

    // Check if user has access
    if (user.subscription !== 'business' || user.role !== 'admin') {
        return (
            <Box minH="100vh" display="flex" flexDirection="column">
                <Navbar />
                <Box display="flex" flex="1" bg={backgroundColor}>
                    <SideBar />
                    <Container maxW="container.xl" flex="1" py={8}>
                        <VStack spacing={6} align="center" justify="center" minH="60vh">
                            <Icon as={FaPalette} boxSize={16} color={mutedTextColor} />
                            <Heading size="lg" color={headingTextColor}>Personalization</Heading>
                            <Text color={bodyTextColor} textAlign="center">
                                This feature is only available for Business subscription users with admin access.
                            </Text>
                        </VStack>
                    </Container>
                </Box>
                <Footer />
            </Box>
        )
    }

    const displayColors = previewColors || (currentThemeId === 'custom' ? customColors : themeData?.colors)

    return (
        <Box minH="100vh" display="flex" flexDirection="column">
            <Navbar />
            <Box display="flex" flex="1" bg={backgroundColor}>
                <SideBar />
                <Container maxW="container.xl" flex="1" py={8}>
                    <VStack spacing={8} align="stretch">
                        {/* Header */}
                        <Box>
                            <Heading size="xl" color={headingTextColor} mb={2}>
                                Personalization
                            </Heading>
                            <Text color={bodyTextColor}>
                                Customize your brand's look and feel with themes and logos
                            </Text>
                        </Box>

                        <Tabs colorScheme="blue" variant="enclosed">
                            <TabList borderColor={borderColor}>
                                <Tab
                                    color={bodyTextColor}
                                    _selected={{ 
                                        color: tabSelectedColor, 
                                        borderColor: tabSelectedColor,
                                        bg: cardBg
                                    }}
                                >
                                    <Icon as={FaPalette} mr={2} />
                                    Themes
                                </Tab>
                                <Tab
                                    color={bodyTextColor}
                                    _selected={{ 
                                        color: tabSelectedColor, 
                                        borderColor: tabSelectedColor,
                                        bg: cardBg
                                    }}
                                >
                                    <Icon as={FaImage} mr={2} />
                                    Logo
                                </Tab>
                            </TabList>

                            <TabPanels>
                                {/* Themes Tab */}
                                <TabPanel px={0}>
                                    <VStack spacing={6} align="stretch">
                                        {/* Live Preview */}
                                        <Card 
                                            bg={displayColors?.background} 
                                            borderWidth="2px" 
                                            borderColor={activeBorderColor}
                                            boxShadow="md"
                                        >
                                            <CardBody>
                                                <VStack spacing={4} align="stretch">
                                                    <Heading size="sm" color={displayColors?.text}>
                                                        Live Preview
                                                    </Heading>
                                                    <HStack spacing={4} flexWrap="wrap">
                                                        <Box
                                                            p={4}
                                                            bg={displayColors?.primary}
                                                            color="white"
                                                            rounded="md"
                                                            flex={1}
                                                            minW="120px"
                                                            textAlign="center"
                                                            fontWeight="semibold"
                                                        >
                                                            Primary
                                                        </Box>
                                                        <Box
                                                            p={4}
                                                            bg={displayColors?.secondary}
                                                            color="white"
                                                            rounded="md"
                                                            flex={1}
                                                            minW="120px"
                                                            textAlign="center"
                                                            fontWeight="semibold"
                                                        >
                                                            Secondary
                                                        </Box>
                                                        <Box
                                                            p={4}
                                                            bg={displayColors?.accent}
                                                            color="white"
                                                            rounded="md"
                                                            flex={1}
                                                            minW="120px"
                                                            textAlign="center"
                                                            fontWeight="semibold"
                                                        >
                                                            Accent
                                                        </Box>
                                                    </HStack>
                                                    <Text color={displayColors?.text} fontSize="sm">
                                                        This is how your text will appear on the background.
                                                    </Text>
                                                </VStack>
                                            </CardBody>
                                        </Card>

                                        {/* Predefined Themes */}
                                        <Box>
                                            <Heading size="md" color={headingTextColor} mb={4}>
                                                Predefined Themes
                                            </Heading>
                                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                                {themes.filter(t => t.id !== 'custom').map((theme) => (
                                                    <Card
                                                        key={theme.id}
                                                        cursor="pointer"
                                                        onClick={() => handleThemeSelect(theme.id)}
                                                        borderWidth="2px"
                                                        borderColor={currentThemeId === theme.id ? activeBorderColor : borderColor}
                                                        bg={cardBg}
                                                        transition="all 0.2s"
                                                        _hover={{
                                                            borderColor: activeBorderColor,
                                                            transform: 'translateY(-2px)',
                                                            shadow: 'lg',
                                                        }}
                                                        position="relative"
                                                    >
                                                        <CardBody>
                                                            <VStack align="stretch" spacing={3}>
                                                                {currentThemeId === theme.id && (
                                                                    <Badge
                                                                        colorScheme="green"
                                                                        position="absolute"
                                                                        top={2}
                                                                        right={2}
                                                                    >
                                                                        <Icon as={FaCheck} mr={1} />
                                                                        Active
                                                                    </Badge>
                                                                )}
                                                                <Heading size="sm" color={headingTextColor}>
                                                                    {theme.name}
                                                                </Heading>
                                                                <Text fontSize="xs" color={bodyTextColor}>
                                                                    {theme.description}
                                                                </Text>
                                                                <HStack spacing={2}>
                                                                    {Object.entries(theme.colors).slice(0, 3).map(([key, value]) => (
                                                                        <Box
                                                                            key={key}
                                                                            w={8}
                                                                            h={8}
                                                                            bg={value}
                                                                            rounded="md"
                                                                            borderWidth="1px"
                                                                            borderColor={borderColor}
                                                                        />
                                                                    ))}
                                                                </HStack>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    borderColor={borderColor}
                                                                    color={strongTextColor}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        handlePreviewTheme(theme)
                                                                    }}
                                                                    _hover={{ bg: isDarkTheme ? 'rgba(66, 153, 225, 0.08)' : 'gray.50' }}
                                                                >
                                                                    Preview
                                                                </Button>
                                                            </VStack>
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                            </SimpleGrid>
                                        </Box>

                                        <Divider borderColor={borderColor} />

                                        {/* Custom Theme */}
                                        <Box>
                                            <Heading size="md" color={headingTextColor} mb={4}>
                                                Custom Theme
                                            </Heading>
                                            <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
                                                <CardBody>
                                                    <VStack spacing={6} align="stretch">
                                                        <Text fontSize="sm" color={bodyTextColor}>
                                                            Create your own unique color scheme
                                                        </Text>

                                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                                            {Object.entries(customColors).map(([key, value]) => (
                                                                <FormControl key={key}>
                                                                    <FormLabel 
                                                                        textTransform="capitalize" 
                                                                        color={labelTextColor}
                                                                        fontWeight="semibold"
                                                                        fontSize="sm"
                                                                    >
                                                                        {key} Color
                                                                    </FormLabel>
                                                                    <HStack>
                                                                        <Box
                                                                            w={12}
                                                                            h={10}
                                                                            bg={value}
                                                                            rounded="md"
                                                                            borderWidth="2px"
                                                                            borderColor={borderColor}
                                                                            cursor="pointer"
                                                                            onClick={() => setActiveColorPicker(activeColorPicker === key ? null : key)}
                                                                            _hover={{ borderColor: activeBorderColor }}
                                                                        />
                                                                        <Input
                                                                            value={value}
                                                                            onChange={(e) => handleCustomColorChange(key, e.target.value)}
                                                                            placeholder="#FFFFFF"
                                                                            maxLength={7}
                                                                            bg={inputBg}
                                                                            color={strongTextColor}
                                                                            borderColor={borderColor}
                                                                            _hover={{ borderColor: activeBorderColor }}
                                                                            _focus={{ borderColor: activeBorderColor }}
                                                                        />
                                                                    </HStack>
                                                                    {activeColorPicker === key && (
                                                                        <Box 
                                                                            mt={2} 
                                                                            p={2} 
                                                                            borderWidth="1px" 
                                                                            borderColor={borderColor}
                                                                            rounded="md"
                                                                            bg={pickerBg}
                                                                        >
                                                                            <HexColorPicker
                                                                                color={value}
                                                                                onChange={(color) => handleCustomColorChange(key, color)}
                                                                            />
                                                                        </Box>
                                                                    )}
                                                                </FormControl>
                                                            ))}
                                                        </SimpleGrid>

                                                        <Button
                                                            bg={primaryColor}
                                                            color="white"
                                                            size="lg"
                                                            onClick={handleSaveCustomTheme}
                                                            isLoading={savingTheme}
                                                            leftIcon={<FaCheck />}
                                                            _hover={{ bg: buttonHoverBg }}
                                                        >
                                                            Save & Apply Custom Theme
                                                        </Button>
                                                    </VStack>
                                                </CardBody>
                                            </Card>
                                        </Box>
                                    </VStack>
                                </TabPanel>

                                {/* Logo Tab */}
                                <TabPanel px={0}>
                                    <VStack spacing={6} align="stretch">
                                        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
                                            <CardBody>
                                                <VStack spacing={6} align="stretch">
                                                    <Box>
                                                        <Heading size="sm" color={headingTextColor} mb={4}>
                                                            Current Logo
                                                        </Heading>
                                                        {logo ? (
                                                            <VStack spacing={4}>
                                                                <Box
                                                                    p={4}
                                                                    borderWidth="1px"
                                                                    borderColor={borderColor}
                                                                    borderRadius="md"
                                                                    bg={isDarkTheme ? 'rgba(45, 55, 72, 0.3)' : 'gray.50'}
                                                                >
                                                                    <Image
                                                                        src={logo}
                                                                        alt="Current logo"
                                                                        maxH="200px"
                                                                        objectFit="contain"
                                                                    />
                                                                </Box>
                                                                <Button
                                                                    colorScheme="red"
                                                                    variant="outline"
                                                                    leftIcon={<FaTrash />}
                                                                    onClick={handleLogoDelete}
                                                                    isLoading={uploading}
                                                                >
                                                                    Delete Logo
                                                                </Button>
                                                            </VStack>
                                                        ) : (
                                                            <Text color={bodyTextColor}>No logo uploaded yet</Text>
                                                        )}
                                                    </Box>

                                                    <Divider borderColor={borderColor} />

                                                    <Box>
                                                        <Heading size="sm" color={headingTextColor} mb={4}>
                                                            Upload New Logo
                                                        </Heading>
                                                        <VStack spacing={4}>
                                                            <Input
                                                                type="file"
                                                                ref={fileInputRef}
                                                                onChange={handleLogoSelect}
                                                                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                                                display="none"
                                                            />
                                                            <Button
                                                                leftIcon={<FaUpload />}
                                                                onClick={() => fileInputRef.current?.click()}
                                                                bg={primaryColor}
                                                                color="white"
                                                                variant="solid"
                                                                _hover={{ bg: buttonHoverBg }}
                                                            >
                                                                Select Logo File
                                                            </Button>

                                                            {logoPreview && (
                                                                <VStack spacing={4} w="full">
                                                                    <Box
                                                                        p={4}
                                                                        borderWidth="1px"
                                                                        borderColor={borderColor}
                                                                        borderRadius="md"
                                                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.3)' : 'gray.50'}
                                                                        w="full"
                                                                    >
                                                                        <Image
                                                                            src={logoPreview}
                                                                            alt="Logo preview"
                                                                            maxH="200px"
                                                                            objectFit="contain"
                                                                        />
                                                                    </Box>
                                                                    <HStack>
                                                                        <Button
                                                                            bg={primaryColor}
                                                                            color="white"
                                                                            onClick={handleLogoUpload}
                                                                            isLoading={uploading}
                                                                            leftIcon={<FaCheck />}
                                                                            _hover={{ bg: buttonHoverBg }}
                                                                        >
                                                                            Upload Logo
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline"
                                                                            borderColor={borderColor}
                                                                            color={strongTextColor}
                                                                            onClick={() => {
                                                                                setLogoFile(null)
                                                                                setLogoPreview(null)
                                                                            }}
                                                                            _hover={{ bg: isDarkTheme ? 'rgba(66, 153, 225, 0.08)' : 'gray.50' }}
                                                                        >
                                                                            Cancel
                                                                        </Button>
                                                                    </HStack>
                                                                </VStack>
                                                            )}

                                                            <Text fontSize="xs" color={mutedTextColor}>
                                                                Supported formats: JPEG, PNG, GIF, WebP (Max 5MB)
                                                            </Text>
                                                        </VStack>
                                                    </Box>
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    </VStack>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </VStack>
                </Container>
            </Box>
            <Footer />

            {/* Preview Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay 
                    backdropFilter="blur(8px)"
                    bg={isDarkTheme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)'}
                />
                <ModalContent 
                    bg={previewColors?.background}
                    borderWidth="1px"
                    borderColor={isDarkTheme ? borderColor : 'transparent'}
                >
                    <ModalHeader color={previewColors?.text}>Theme Preview</ModalHeader>
                    <ModalCloseButton color={previewColors?.text} />
                    <ModalBody pb={6}>
                        <VStack spacing={4} align="stretch">
                            <Box p={6} bg={previewColors?.primary} color="white" rounded="lg">
                                <Heading size="md" mb={2}>Primary Color</Heading>
                                <Text>This is your main brand color - used for buttons, links, and key elements</Text>
                            </Box>
                            <Box p={6} bg={previewColors?.secondary} color="white" rounded="lg">
                                <Heading size="md" mb={2}>Secondary Color</Heading>
                                <Text>Used for complementary elements and hover states</Text>
                            </Box>
                            <Box p={6} bg={previewColors?.accent} color="white" rounded="lg">
                                <Heading size="md" mb={2}>Accent Color</Heading>
                                <Text>Highlights and special emphasis elements</Text>
                            </Box>
                            <Box 
                                p={6} 
                                borderWidth="1px" 
                                borderColor={isDarkTheme ? borderColor : 'gray.200'}
                                rounded="lg"
                            >
                                <Heading size="md" mb={2} color={previewColors?.text}>Background & Text</Heading>
                                <Text color={previewColors?.text}>
                                    This demonstrates how your text appears on the background color. 
                                    Make sure there's good contrast for readability.
                                </Text>
                            </Box>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    )
}

export default Personalization